import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';

export type OrderList = ResponseBody<Operation<'/admin/orders', 'get'>>;
export type Order = OrderList['items'][number];
export type OrderDetail = ResponseBody<Operation<'/admin/orders/{id}', 'get'>>;
export type CreateOrderBody = RequestBody<Operation<'/admin/orders', 'post'>>;
export type AmendOrderBody = RequestBody<Operation<'/admin/orders/{id}', 'patch'>>;
export type OrderHistoryEntry = OrderDetail['history'][number];
export type OrderStatus = Order['status'];
export type OrderSource = NonNullable<CreateOrderBody['source']>;

export const ORDER_SOURCES = [
  'facebook',
  'instagram',
  'tiktok',
  'whatsapp',
  'phone',
  'other',
] as const satisfies readonly OrderSource[];

export type CancelReason = RequestBody<Operation<'/admin/orders/{id}/cancel', 'post'>>['reason'];

export type OrderView = NonNullable<
  NonNullable<Operation<'/admin/orders', 'get'>['parameters']['query']>['view']
>;

export const ORDER_VIEWS = [
  'all',
  'to_confirm',
  'confirmed',
  'to_print',
  'printed',
  'in_delivery',
  'missing',
  'delivered',
  'returns',
  'to_receive',
  'exchanges',
  'canceled',
] as const satisfies readonly OrderView[];

export const PRIMARY_VIEWS = [
  'all',
  'to_confirm',
  'confirmed',
  'in_delivery',
  'delivered',
  'returns',
] as const satisfies readonly OrderView[];

export const ATTENTION_VIEWS = ['to_print', 'missing', 'to_receive'] as const satisfies readonly OrderView[];

export const MORE_VIEWS = ['printed', 'exchanges', 'canceled'] as const satisfies readonly OrderView[];

export const chipViewsOf = (
  counts: ReadonlyMap<OrderView, number> | undefined,
  current: OrderView,
): readonly OrderView[] => [
  ...PRIMARY_VIEWS,
  ...ATTENTION_VIEWS.filter((view) => view === current || (counts?.get(view) ?? 0) > 0),
];

export type ViewCounts = ResponseBody<Operation<'/admin/orders/views', 'get'>>['counts'];

export const CANCEL_REASONS: readonly CancelReason[] = [
  'customer_declined',
  'unreachable',
  'duplicate',
  'out_of_stock',
  'fraud_suspected',
  'other',
];

export const ORDERS_CONFIRM_ROLE = 'orders:confirm';
export const ORDERS_DISPATCH_ROLE = 'orders:dispatch';

export const AWAITING_CARRIER: OrderStatus = 'DISPATCH_REQUESTED';

export const STOCK_RESOURCES = ['orders', 'stock-levels', 'low-stock', 'dashboard'] as const;

const BEFORE_DISPATCH: readonly OrderStatus[] = ['PENDING', 'CONFIRMED'];

export const isBeforeDispatch = (order: Pick<Order, 'status'>): boolean =>
  BEFORE_DISPATCH.includes(order.status);

const AT_CARRIER_BEFORE_PICKUP: readonly OrderStatus[] = ['DISPATCH_REQUESTED', 'DISPATCHED'];

export const isAtCarrierBeforePickup = (order: Pick<Order, 'status'>): boolean =>
  AT_CARRIER_BEFORE_PICKUP.includes(order.status);

const EDITABLE_AT_CARRIER: OrderStatus = 'DISPATCHED';

export const isEditableAtCarrier = (
  order: Pick<Order, 'status' | 'dispatch'>,
  acceptsChanges: (carrierCode: string) => boolean,
): boolean =>
  order.status === EDITABLE_AT_CARRIER &&
  order.dispatch.carrierCode !== null &&
  acceptsChanges(order.dispatch.carrierCode);

export const isCancelable = (order: Pick<Order, 'status'>): boolean =>
  isBeforeDispatch(order) || isAtCarrierBeforePickup(order);

export const hasPrintableLabel = (order: Pick<Order, 'dispatch'>): boolean =>
  order.dispatch.shipmentId !== null && order.dispatch.trackingNumber !== null;

export const CARRIER_FOLLOW_INTERVAL_MS = 3000;

export const amountOf = (order: Pick<Order, 'currency'>, amount: string) => ({
  amount,
  currency: order.currency,
});
